import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { LogOut, Building2, GitBranch, User, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import logoutUser from "../../utils/logout";

const statusOptions = ["NotAttended", "Consulting", "Completed"];

const NurseDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("all");
  const [doctors, setDoctors] = useState([]);
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [nurseInfo, setNurseInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const date = new Date();
    const formatted = date.toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    setToday(formatted);
    fetchNurseInfo();
    fetchAppointments();
  }, []);

  useEffect(() => {
    // Filter appointments by selected doctor
    if (selectedDoctor === "all") {
      setFilteredAppointments(appointments);
    } else {
      setFilteredAppointments(
        appointments.filter(
          (appt) => appt.doctorSchedule?.doctor?.id === parseInt(selectedDoctor)
        )
      );
    }
  }, [selectedDoctor, appointments]);

  const fetchNurseInfo = async () => {
    try {
      const res = await axiosInstance.get("/users/me");
      setNurseInfo(res.data.user || null);
    } catch (error) {
      console.error("Failed to fetch nurse info:", error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const res = await axiosInstance.get("/nurses/appointments/today");
      const appointmentsData = res.data.appointments || [];
      setAppointments(appointmentsData);

      // Extract unique doctors from appointments
      const uniqueDoctors = [];
      const doctorIds = new Set();

      appointmentsData.forEach((appt) => {
        const doctor = appt.doctorSchedule?.doctor;
        if (doctor && !doctorIds.has(doctor.id)) {
          doctorIds.add(doctor.id);
          uniqueDoctors.push(doctor);
        }
      });

      setDoctors(uniqueDoctors);
    } catch (error) {
      console.error("Failed to fetch appointments:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await axiosInstance.post(
        `/nurses/appointments/${bookingId}/visit-status`,
        {
          visitStatus: newStatus,
        }
      );

      setAppointments((prev) =>
        prev.map((a) =>
          a.id === bookingId ? { ...a, visitStatus: newStatus } : a
        )
      );
    } catch (error) {
      console.error("Failed to update visit status:", error);
    }
  };

  const isDisabled = (current, target) => {
    if (current === "Consulting" && target === "NotAttended") return true;
    if (current === "Completed" && target !== "Completed") return true;
    return false;
  };

  const getSegmentStyle = (status, currentStatus, index, disabled) => {
    const base =
      "text-sm px-4 py-1 border transition font-medium focus:outline-none";
    const rounded =
      index === 0
        ? "rounded-l-full"
        : index === statusOptions.length - 1
        ? "rounded-r-full"
        : "";

    const selected =
      status === currentStatus
        ? status === "Completed"
          ? "bg-green-600 text-white"
          : status === "Consulting"
          ? "bg-blue-600 text-white"
          : "bg-yellow-500 text-white"
        : "bg-white text-gray-700 hover:bg-gray-100";

    const disabledStyle = disabled
      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
      : "";

    return `${base} ${rounded} ${disabled ? disabledStyle : selected}`;
  };

  const handleLogout = async () => {
    const success = await logoutUser();
    if (success) {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Custom Header with Nurse Info */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <span className="text-3xl">🩺</span>
                  Nurse Dashboard
                </h1>
                <p className="text-sm text-gray-500 mt-1">{today}</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              {/* Nurse Information */}
              {nurseInfo && (
                <div className="hidden md:flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                    <User className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">
                      {nurseInfo.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                    <GitBranch className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-800">
                      {nurseInfo.branch?.name || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-lg border border-purple-200">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-800">
                      {nurseInfo.branch?.hospital?.name || "N/A"}
                    </span>
                  </div>
                </div>
              )}

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 border border-red-200"
              >
                <LogOut className="w-4 h-4" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Nurse Info */}
          {nurseInfo && (
            <div className="md:hidden mt-4 space-y-2">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200 text-sm">
                <User className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-800">
                  {nurseInfo.name}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200 text-sm">
                <GitBranch className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-800">
                  {nurseInfo.branch?.name || "N/A"}
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-200 text-sm">
                <Building2 className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">
                  {nurseInfo.branch?.hospital?.name || "N/A"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Doctor Filter */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2 text-gray-700 font-medium text-sm sm:text-base">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Filter by Doctor:</span>
                  <span className="sm:hidden">Doctor:</span>
                </div>

                {/* All Doctors Button */}
                <button
                  onClick={() => setSelectedDoctor("all")}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
                    selectedDoctor === "all"
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  All ({appointments.length})
                </button>

                {/* Individual Doctor Buttons */}
                {doctors.map((doctor) => {
                  const count = appointments.filter(
                    (appt) => appt.doctorSchedule?.doctor?.id === doctor.id
                  ).length;

                  return (
                    <button
                      key={doctor.id}
                      onClick={() => setSelectedDoctor(doctor.id.toString())}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 text-sm rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                        selectedDoctor === doctor.id.toString()
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <span className="truncate max-w-[120px] sm:max-w-none">
                        Dr. {doctor.name}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${
                          selectedDoctor === doctor.id.toString()
                            ? "bg-white text-blue-600"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Appointments Count */}
            <div className="mb-4 text-xs sm:text-sm text-gray-600">
              Showing{" "}
              <span className="font-semibold text-gray-900">
                {filteredAppointments.length}
              </span>{" "}
              appointment{filteredAppointments.length !== 1 ? "s" : ""}
              {selectedDoctor !== "all" && (
                <button
                  onClick={() => setSelectedDoctor("all")}
                  className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm"
                >
                  Clear filter
                </button>
              )}
            </div>

            {/* Appointments Grid */}
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
                <p className="text-gray-500 text-base sm:text-lg">
                  {selectedDoctor === "all"
                    ? "No appointments scheduled for today"
                    : "No appointments for this doctor today"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredAppointments.map((appt) => (
                  <div
                    key={appt.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 hover:shadow-md transition"
                  >
                    <h2 className="text-base sm:text-lg font-semibold text-gray-800 truncate">
                      {appt.patient?.name || "Unknown"}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      📞 {appt.patient?.phoneNumber}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Age: {appt.patient?.age} yrs | {appt.patient?.gender}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      👨‍⚕️ Dr. {appt.doctorSchedule?.doctor?.name || "Unknown"}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      🕒 {appt.doctorSchedule?.startTime} -{" "}
                      {appt.doctorSchedule?.endTime}
                    </p>

                    <div className="mt-4 inline-flex rounded-full overflow-hidden border border-gray-300 w-full sm:w-auto">
                      {statusOptions.map((status, index) => {
                        const disabled = isDisabled(appt.visitStatus, status);
                        return (
                          <button
                            key={status}
                            disabled={disabled}
                            onClick={() =>
                              !disabled && handleStatusChange(appt.id, status)
                            }
                            className={getSegmentStyle(
                              status,
                              appt.visitStatus,
                              index,
                              disabled
                            )}
                          >
                            <span className="hidden sm:inline">{status}</span>
                            <span className="sm:hidden text-xs">
                              {status === "NotAttended"
                                ? "Not"
                                : status === "Consulting"
                                ? "Cons"
                                : "Done"}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-4 text-xs sm:text-sm text-gray-700">
                      Status:{" "}
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          appt.visitStatus === "NotAttended"
                            ? "bg-yellow-100 text-yellow-800"
                            : appt.visitStatus === "Consulting"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {appt.visitStatus}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NurseDashboard;
